import React, {
  forwardRef,
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { polyfill as scrollPolyfill } from "seamless-scroll-polyfill";
import {
  Box,
  Typography,
  useTheme,
  Button,
  tableHeadClasses,
} from "@mui/material";
import ShuffleIcon from "@mui/icons-material/Shuffle";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import ScrollableWrapper from "../ScrollableWrapper";
import { SelectedSummary } from "../SelectedSummary";
import { ExportOptions } from "../ExportOptions";
import { OptionGroup } from "../OptionGroup";
import {
  InteractiveButtonsRoot,
  ContentLeft,
  ContentRight,
  LeftContent,
  Categories,
  Category,
  SubCategories,
  OptionsBlock,
  ToggleMenuBtn,
} from "./InteractiveButtons.styles";
import { findByDescriptor } from "../../helpers/findItems";
import { generateRandomConfiguration } from "../../helpers/randomizer";
import { useSelectedOptions } from "../../context/SelectedOptionsContext";
import { useRouteParams } from "../../context/RouteParamsContext";
import { parseUrlConfigString } from "../../helpers/config-utilities";
import { useDevice } from "../../hooks/useDevice";

/* ---------------- helpers ---------------- */

function normalizeDescriptors(list = []) {
  const out = [];
  for (const d of list) {
    if (!d || typeof d !== "object") continue;
    const keys = Object.keys(d);
    if (keys.length !== 1) continue;
    const key = keys[0];
    out.push({ key, value: d[key], descriptor: d });
  }
  return out;
}

// parse app response into [{key:..., value:..., descriptor:{key:value}}, ...]
function parseDescriptorsFromAppResponse(resp) {
  if (typeof resp !== "string") return [];
  const out = [];
  try {
    const j = JSON.parse(resp);
    if (j && typeof j === "object") {
      Object.keys(j).forEach((k) => out.push({ [k]: j[k] }));
      return out;
    }
  } catch {}
  const pairs = [
    ...resp.matchAll(/"([A-Za-z0-9_]+)"\s*:\s*(?:"([^"]+)"|([0-9.]+))/g),
  ];
  for (const m of pairs) {
    const key = m[1];
    const val = m[2] ?? m[3];
    const coerced = m[3] ? Number(val) : val;
    out.push({ [key]: coerced });
  }
  return out;
}

/* -------------- component -------------- */

const InteractiveButtons = forwardRef((props, ref) => {
  const {
    emitUIInteraction,
    actionsList,
    className,
    applicationResponse,
    loading,
    onStartExport,
    showingOverlay,
  } = props;

  const theme = useTheme();
  const { isPortrait } = useDevice();

  const {
    selectOption,
    map: selectedMap, // single source of truth (hydrated + persisted)
  } = useSelectedOptions();

  const { configCode } = useRouteParams();
  const hasLoadedFromUrl = useRef(false);

  const rootRef = useRef(null);
  const categoriesRef = useRef(null);
  const [panelHeight, setPanelHeight] = useState(0);
  const [categoriesHeight, setCategoriesHeight] = useState(0);

  useEffect(() => {
    const measure = () => {
      if (rootRef.current) {
        setPanelHeight(rootRef.current.offsetHeight || 0);
      }
      if (categoriesRef.current) {
        setCategoriesHeight(categoriesRef.current.offsetHeight || 0);
      }
    };

    // initial measure
    measure();

    // observe for dynamic changes
    const ro = new ResizeObserver(measure);
    if (rootRef.current) ro.observe(rootRef.current);
    if (categoriesRef.current) ro.observe(categoriesRef.current);

    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    scrollPolyfill({ duration: 90 });
  }, []);

  // Fallback defaults (only used if app + local are missing a key)
  const DEFAULT_SELECTIONS = useMemo(
    () => [
      { descriptor: { widebody: true } },
      { descriptor: { ExPt: "prm" } },
      { descriptor: { WhMo: "kpr" } },
      { descriptor: { WhCo: "hs" } },
      { descriptor: { BrCo: "pym" } },
      { descriptor: { TiMo: "to" } },
      { descriptor: { FrLi: "wcf" } },
      { descriptor: { FeLo: "wcf" } },
      { descriptor: { SiSk: "wcf" } },
      { descriptor: { SiBl: "wcf" } },
      { descriptor: { MiCa: "wcf" } },
      { descriptor: { ReDi: "wcf" } },
      { descriptor: { SpAc: "on" } },
      { descriptor: { SpCo: "wcf" } },
      { descriptor: { SpPl: "wcf" } },
      { descriptor: { ExTp: "1" } },
      { descriptor: { ExMa: "ss" } },
      { descriptor: { InCo: "leblta" } },
      { descriptor: { FuHe: "on" } },
      { descriptor: { FuTl: "on" } },
      { descriptor: { FuWi: "up" } },
      { descriptor: { FuFl: "off" } },
      { descriptor: { FuMw: "0.5" } },
      { descriptor: { MeWe: "pc" } },
      { descriptor: { CaEx: "34fr" } },
      { descriptor: { CaIn: "dd" } },
      { descriptor: { MeBg: "rt" } },
    ],
    []
  );

  useEffect(() => {
    // CRITICAL CHECK 1: Ensure actionsList is loaded (same as before)
    if (!Array.isArray(actionsList) || actionsList.length === 0) {
      return;
    }

    // CRITICAL CHECK 2: ONLY hydrate if the context state is empty.
    // If selectedMap is NOT empty, it means the context has ALREADY been
    // hydrated by localStorage (from the fix in SelectedOptionsContext.js).
    if (Object.keys(selectedMap).length > 0) {
      return;
    }

    // --- BUILD PRIORITY MAP (Only focusing on App Response and Defaults) ---

    // 1. Highest Priority: applicationResponse
    const descriptorsByGroupKey = {};
    const appDescriptors = parseDescriptorsFromAppResponse(applicationResponse);
    appDescriptors.forEach((desc) => {
      const groupKey = Object.keys(desc)[0];
      if (groupKey) descriptorsByGroupKey[groupKey] = desc;
    });

    // 2. Lowest Priority: DEFAULT_SELECTIONS (Only used if no App Response)
    DEFAULT_SELECTIONS.forEach((item) => {
      const desc = item.descriptor;
      const groupKey = Object.keys(desc)[0];
      // Only fill groups not already set by appResponse
      if (groupKey && !descriptorsByGroupKey[groupKey]) {
        descriptorsByGroupKey[groupKey] = desc;
      }
    });

    // --- HYDRATE CONTEXT ---
    const descriptorsToSelect = Object.values(descriptorsByGroupKey);

    descriptorsToSelect.forEach((descriptor) => {
      // Find the full option object
      const fullOption = findByDescriptor(actionsList, descriptor);

      if (fullOption) {
        selectOption(fullOption);
      }
    });
  }, [
    DEFAULT_SELECTIONS,
    actionsList,
    applicationResponse,
    selectOption,
    selectedMap,
  ]);

  const [firstLevelOptionIndex, setFirstLevelOptionIndex] = useState(() => {
    const saved = localStorage.getItem("firstLevelOptionIndex");
    return saved !== null ? parseInt(saved, 10) : 0;
  });

  const [secondLevelOptionIndex, setSecondLevelOptionIndex] = useState(() => {
    const saved = localStorage.getItem("secondLevelOptionIndex");
    return saved !== null ? parseInt(saved, 10) : 0;
  });

  const [mainActions, setMainActions] = useState([]);
  const [toggleInterface, setToggleInterface] = useState(true);
  const [hovered, setHovered] = useState({ idx: null, name: null });

  const handleToggleInterface = (value) => {
    setToggleInterface(!value);
  };

  const translateY = toggleInterface ? 0 : -(panelHeight - categoriesHeight);

  // split actionsList into mainActions
  useEffect(() => {
    const arr = [];
    actionsList?.forEach((item) => arr.push(item));
    setMainActions(arr);
  }, [actionsList]);

  useEffect(() => {
    const orientation = isPortrait ? { zoom: "-37.5" } : { zoom: "0" };
    clickOnAction(orientation);
  }, [isPortrait]);

  const clickOnAction = useCallback(
    (descriptor) => {
      emitUIInteraction(descriptor);
    },
    [emitUIInteraction]
  );

  useEffect(() => {
    // 🛑 GUARD 1: Block loading if the overlay is showing
    if (showingOverlay) return;

    // 🛑 GUARD 2: Block if already processed from URL
    if (hasLoadedFromUrl.current) return;

    // 🛑 GUARD 3: No config code? Nothing to load.
    if (!configCode) {
      hasLoadedFromUrl.current = true;
      return;
    }

    // 1️⃣ Parse the configuration string into { groupKey: value }
    const descriptorMap = parseUrlConfigString(configCode);
    if (Object.keys(descriptorMap).length === 0) {
      hasLoadedFromUrl.current = true;
      return;
    }

    // Helper — detect "r=...,g=...,b=...,a=..." color values
    const isFloatRgbaString = (val) =>
      typeof val === "string" &&
      /^r=\d*\.?\d+,\s*g=\d*\.?\d+,\s*b=\d*\.?\d+,\s*a=\d*\.?\d+$/i.test(val);

    // Helper — find the RGB/IPF node for a given group
    function findCustomRgbNode(actionsList, groupKey) {
      const ipfMatch = findByDescriptor(actionsList, { [groupKey]: "ipf" });
      if (ipfMatch?.node) return ipfMatch;

      // fallback: manually scan for nodes marked customRGB
      const stack = [...(actionsList || [])];
      while (stack.length) {
        const n = stack.pop();
        if (n?.subActions?.length) stack.push(...n.subActions);
        if (n?.action?.descriptor && n?.customRGB === true) {
          const key = Object.keys(n.action.descriptor)[0];
          if (key === groupKey) return { node: n, sub: null };
        }
      }
      return null;
    }

    // 2️⃣ Loop over each descriptor and apply it
    Object.entries(descriptorMap).forEach(([groupKey, descriptorValue]) => {
      const currentOption = selectedMap[groupKey];
      const currentDescriptorValue = currentOption?.descriptor?.[groupKey];

      // Normalize boolean strings
      let normalizedDescriptorValue = descriptorValue;
      if (normalizedDescriptorValue === "true")
        normalizedDescriptorValue = true;
      if (normalizedDescriptorValue === "false")
        normalizedDescriptorValue = false;

      const isFloat = isFloatRgbaString(normalizedDescriptorValue);

      // Skip redundant updates
      if (isFloat) {
        if (currentOption?.color === normalizedDescriptorValue) return;
      } else {
        if (currentDescriptorValue === normalizedDescriptorValue) return;
      }

      // Find the full option in the actions list
      const descriptor = { [groupKey]: normalizedDescriptorValue };
      let optionContext = findByDescriptor(actionsList, descriptor);

      // If not found, check if this is a float RGBA color
      if (!optionContext && isFloat) {
        optionContext = findCustomRgbNode(actionsList, groupKey);
      }

      if (optionContext?.node) {
        const { node, sub } = optionContext;

        // ✅ Store the float RGBA as the descriptor value (not "ipf")
        const storedDescriptor = isFloat
          ? { [groupKey]: normalizedDescriptorValue }
          : node.action?.descriptor || descriptor;

        // What to send to Unreal
        const engineDescriptor = isFloat
          ? { [groupKey]: normalizedDescriptorValue }
          : storedDescriptor;

        const payload = {
          name: node.name,
          image: node.image,
          price: node.price,
          descriptor: storedDescriptor,
          baseOption: node.action?.baseOption ?? node.baseOption,
          inSummary: node.action?.inSummary ?? node.inSummary,
          subCategory: sub?.name,
          groupKey,
          ...(isFloat ? { color: normalizedDescriptorValue } : {}),
        };

        // A) Update context
        selectOption(payload);

        // B) Send to Unreal Engine
        clickOnAction?.(engineDescriptor);
      }
    });

    // 3️⃣ Prevent this effect from running again
    hasLoadedFromUrl.current = true;
  }, [
    configCode,
    selectOption,
    actionsList,
    clickOnAction,
    selectedMap,
    showingOverlay,
  ]);

  // at top:
  const didHydrateRef = React.useRef(false);

  // REPLACE your current hydration useEffect with this:
  useEffect(() => {
    const hasActions = Array.isArray(mainActions) && mainActions.length > 0;
    if (!hasActions || didHydrateRef.current) return;

    // lowest: defaults
    const defaultsArr = DEFAULT_SELECTIONS.map((d) => d.descriptor);

    // middle: selectedOptions_v1 (persisted context)
    let lsArr = [];
    try {
      const lsObj = JSON.parse(
        localStorage.getItem("selectedOptions_v1") || "{}"
      );
      lsArr = Object.values(lsObj)
        .map((e) => e.descriptor)
        .filter(Boolean);
    } catch {}

    // normalize:
    const nDefaults = normalizeDescriptors(defaultsArr);
    const nLS = normalizeDescriptors(lsArr);

    // merge (defaults -> local) later wins
    const merged = new Map();
    for (const it of nDefaults) merged.set(it.key, it);
    for (const it of nLS) merged.set(it.key, it);

    // seed context
    merged.forEach(({ descriptor }) => {
      const name = findByDescriptor(mainActions, descriptor, {
        select: ({ node }) => node?.name,
      });
      const image = findByDescriptor(mainActions, descriptor, {
        select: ({ node }) => node?.image,
      });
      const price = findByDescriptor(mainActions, descriptor, {
        select: ({ node }) => node?.price,
      });
      const subCategory =
        findByDescriptor(mainActions, descriptor, {
          select: ({ sub }) => sub?.name,
        }) || "";
      const inSummary = findByDescriptor(mainActions, descriptor, {
        select: ({ node }) => node?.inSummary,
      });
      const baseOption = findByDescriptor(mainActions, descriptor, {
        select: ({ sub }) => sub?.baseOption,
      });

      selectOption({
        name,
        image,
        price,
        baseOption,
        descriptor,
        subCategory,
        inSummary,
      });
    });

    didHydrateRef.current = true;
  }, [mainActions]); // <-- IMPORTANT: no applicationResponse, no selectedMap, no selectOption

  const lastAppRespRef = React.useRef(null);

  // ####################################################
  // fix this later
  useEffect(() => {
    if (!didHydrateRef.current) return; // wait until we seeded from defaults/LS
    if (lastAppRespRef.current === applicationResponse) return;

    const appArr = parseDescriptorsFromAppResponse(applicationResponse);

    if (!appArr.length) {
      lastAppRespRef.current = applicationResponse || null;
      return;
    }
    const nApp = normalizeDescriptors(appArr);
    const changed = [];

    for (const it of nApp) {
      const key = it.key;
      const newVal = it.value;
      const descriptor = { [key]: newVal };

      // 1) If we already have this group in context/localStorage,
      //    keep everything and only update the descriptor (+ color for FloatRGB).
      const existing = selectedMap?.[key]; // shape stored in selectedOptions_v1

      if (existing) {
        const keep = { ...existing, descriptor };

        // If UE sent a FloatRGB (e.g. "r=0.086,g=0.710,b=0.439,a=1"),
        // persist it so the ColorWheel can position itself.
        if (typeof newVal === "string" && newVal.startsWith("r=")) {
          keep.color = newVal;
        }

        changed.push(keep);
        continue;
      }

      // 2) Otherwise, try to resolve the full option from the tree.
      const found = findByDescriptor(mainActions, descriptor); // { node, sub } or null
      const name = found?.node?.name ?? null;
      const image = found?.node?.image ?? null;
      const price = found?.node?.price ?? 0;
      const subCategory = found?.sub?.name ?? "";
      const inSummary =
        found?.node?.inSummary ?? found?.node?.action?.inSummary ?? null;
      const baseOption =
        found?.node?.baseOption ?? found?.node?.action?.baseOption ?? null;

      const payload = {
        name,
        image,
        price,
        baseOption,
        descriptor,
        subCategory,
        inSummary,
      };

      if (typeof newVal === "string" && newVal.startsWith("r=")) {
        payload.color = newVal;
      }

      changed.push(payload);
    }

    if (changed.length) {
      changed.forEach(selectOption);
    }

    lastAppRespRef.current = applicationResponse || null;
  }, [applicationResponse, mainActions, selectedMap, selectOption]);

  // ——— derive UI stuff from context ———

  // ####################################################
  const subList = mainActions[firstLevelOptionIndex]?.subActions ?? [];
  const selected = secondLevelOptionIndex;

  // All leaf nodes
  function collectLeavesFull(nodes = [], acc = []) {
    for (const n of nodes) {
      if (n?.subActions?.length > 0) {
        collectLeavesFull(n.subActions, acc);
      } else if (
        n?.action?.descriptor &&
        typeof n.action.descriptor === "object"
      ) {
        acc.push(n);
      }
    }
    return acc;
  }

  function findLeafByNameInSub(subCategoryNode, name) {
    if (!subCategoryNode || !name) return null;
    const leaves = collectLeavesFull(subCategoryNode.subActions || []);
    return leaves.find((leaf) => leaf?.name === name) || null;
  }

  function getActiveLeafForSubcategory(subCategoryNode, selectedMap) {
    if (!subCategoryNode) return null;
    const leaves = collectLeavesFull(subCategoryNode.subActions || []);
    for (const leaf of leaves) {
      const desc = leaf?.action?.descriptor;
      if (!desc) continue;
      const key = Object.keys(desc)[0];
      if (!key) continue;
      const selectedEntry = selectedMap?.[key];
      if (selectedEntry?.descriptor?.[key] === desc[key]) {
        return leaf;
      }
    }
    return null;
  }

  const checkIfOptionIsActive = (option) => {
    if (!option || typeof option !== "object") return false;
    const [key, value] = Object.entries(option)[0] || [];
    const selectedVal = selectedMap?.[key]?.descriptor?.[key];
    return JSON.stringify(selectedVal) === JSON.stringify(value);
  };

  const formatCurrency = (n) =>
    new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    }).format(Number(n) || 0);

  const currentSub = subList[selected] || null;

  const activeLeaf = useMemo(
    () => getActiveLeafForSubcategory(currentSub, selectedMap),
    [currentSub, selectedMap]
  );

  const hoveredLeaf = useMemo(
    () =>
      hovered.idx === selected && hovered.name
        ? findLeafByNameInSub(currentSub, hovered.name)
        : null,
    [hovered, selected, currentSub]
  );

  const displayLeaf = hoveredLeaf ?? activeLeaf;
  const displayName = displayLeaf?.name ?? " ";
  const displayPrice = displayLeaf?.price ?? null;

  const activeFilteredSubActions = mainActions[
    firstLevelOptionIndex
  ]?.subActions?.filter((i) => i.baseOption !== true);

  const selectAndEmit = (descriptor) => {
    if (!descriptor || typeof descriptor !== "object") return;

    // 1) Send to UE
    clickOnAction(descriptor);

    // 2) Find the full node for this descriptor in your tree
    const found = findByDescriptor(actionsList, descriptor);
    if (!found || !found.node) return; // not a leaf or not found → nothing to store

    const { node, sub } = found;

    // 3) Build the same shape OptionGroup uses for selectOption
    selectOption({
      name: node.name,
      image: node.image,
      price: node.price ?? 0,
      descriptor: node.action?.descriptor || descriptor,
      baseOption: node.action?.baseOption ?? node.baseOption,
      inSummary: node.action?.inSummary ?? node.inSummary,
      subCategory: sub?.name || "",
    });
  };

  const handleRandomize = React.useCallback(() => {
    // 1. Generate the random descriptors map (e.g., { FrLi: "wcf", ExPt: "prm", ... })
    const randomDescriptors = generateRandomConfiguration();

    // 2. Iterate and select each randomized option
    Object.entries(randomDescriptors).forEach(([groupKey, descriptorValue]) => {
      // Construct the descriptor object (e.g., { "FrLi": "wcf" })
      const descriptor = { [groupKey]: descriptorValue };

      // Get the full context object: { level1, sub, node } (using default selector)
      const optionContext = findByDescriptor(actionsList, descriptor);

      if (optionContext && optionContext.node) {
        const { node, sub } = optionContext;

        // 3. CRITICAL: Map the context data into the required flat shape for selectOption
        const payload = {
          // Required properties, typically found on the 'node'
          name: node.name,
          image: node.image,
          price: node.price,

          // Properties often nested or on related objects
          descriptor: node.action?.descriptor || descriptor, // Use descriptor from action or fall back to the generated one
          baseOption: node.action?.baseOption || node.baseOption, // Find base option
          inSummary: node.action?.inSummary || node.inSummary, // Find inSummary status

          // SubCategory name comes from the 'sub' action wrapper
          subCategory: sub?.name,

          // Note: The context internally calculates groupKey from the descriptor.
        };

        // 4. Update the context with the correctly shaped payload
        selectOption(payload);
        clickOnAction(randomDescriptors);
      }
    });
  }, [actionsList, selectOption]); // actionsList and the stable selectOption

  return (
    !loading && (
      <InteractiveButtonsRoot
        className={className}
        show={toggleInterface}
        ref={rootRef}
        sx={{
          bottom: `${translateY}px`,
          willChange: "transform",
        }}
      >
        <ToggleMenuBtn
          onClick={() => handleToggleInterface(toggleInterface)}
          show={toggleInterface}
        >
          <ExpandMoreIcon fontSize="large" />
        </ToggleMenuBtn>

        <ContentLeft>
          {/* First level */}
          <Categories
            ref={categoriesRef}
            show={toggleInterface}
            value={firstLevelOptionIndex}
            onChange={(_, v) => {
              setFirstLevelOptionIndex(v);
              setSecondLevelOptionIndex(0);
              localStorage.setItem("firstLevelOptionIndex", v);
              setHovered({ idx: null, name: null });
              selectAndEmit(mainActions[v]?.action?.descriptor);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
          >
            {mainActions.map((item, index) => (
              <Category
                key={`${item.name}-${index}`}
                label={item.name}
                divider
                heightDivider="70%"
              />
            ))}
            <Button
              startIcon={<ShuffleIcon />}
              variant="contained"
              sx={{
                zIndex: 9,
                color: theme?.palette?.companyColors?.neutral,
                background: theme?.palette?.companyColors?.accent,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: theme?.borderRadius?.small,
                minWidth: "125px",
                alignSelf: "center",
                margin: { xs: "-1px 5rem 0 1rem", lg: "0 1rem" },
                boxShadow: "none",
                padding: {
                  xs: "0.5rem 1rem",
                  lg: "1rem 1.5rem",
                  xl: ".5rem 2rem",
                },
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleRandomize();
              }}
            >
              <Typography
                variant="button"
                sx={{
                  textTransform: "initial",
                  fontSize: { xs: "1.75rem", lg: "1.5rem", xl: "1.75rem" },
                  fontWeight: 400,
                }}
              >
                Randomizer
              </Typography>
            </Button>
          </Categories>

          <LeftContent>
            {/* Second level */}
            <SubCategories
              value={secondLevelOptionIndex}
              onChange={(_, v) => {
                setSecondLevelOptionIndex(v);
                localStorage.setItem("secondLevelOptionIndex", v);
                setHovered({ idx: null, name: null });
              }}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
            >
              {(activeFilteredSubActions ?? []).map((sub, idx) => {
                if (sub?.baseOption) return undefined;
                return (
                  <Category
                    key={`${sub.name}-${idx}`}
                    label={sub.name}
                    divider={activeFilteredSubActions?.length !== idx + 1}
                    heightDivider="70%"
                    sx={{ padding: "1.25rem 2.5rem 1rem" }}
                    onClick={() => selectAndEmit(sub?.action?.descriptor)}
                  />
                );
              })}
            </SubCategories>

            {subList.map((item, idxOption) => {
              const isSelected = selected === idxOption;
              const subCategoryName = item?.name || "";
              if (item?.baseOption === true) return undefined;

              return (
                <React.Fragment key={`${item?.name}-${idxOption}`}>
                  {isSelected && (
                    <ScrollableWrapper>
                      {item?.name === "Export Image" && (
                        <ExportOptions
                          clickOnAction={clickOnAction}
                          onStartExport={onStartExport}
                        />
                      )}

                      {(item?.subActions ?? []).map(
                        (innerItem, indexInnerItem) => {
                          const hasChildren = innerItem?.subActions?.length > 0;
                          if (!hasChildren) return null;

                          return (
                            <OptionsBlock
                              key={`${innerItem.name}${indexInnerItem + 2}`}
                              divider={
                                item.subActions?.length !== indexInnerItem + 1
                              }
                            >
                              <OptionGroup
                                title={innerItem?.name}
                                items={innerItem?.subActions}
                                checkIfOptionIsActive={checkIfOptionIsActive}
                                clickOnAction={clickOnAction}
                                onHoverChange={(name) =>
                                  setHovered({
                                    idx: idxOption,
                                    name: name ?? null,
                                  })
                                }
                                onSelectOption={(payload) =>
                                  selectOption(payload)
                                }
                                selectedMap={selectedMap}
                                subCategoryName={subCategoryName}
                              />
                            </OptionsBlock>
                          );
                        }
                      )}
                    </ScrollableWrapper>
                  )}
                </React.Fragment>
              );
            })}

            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "flex-start",
                alignItems: "center",
                zIndex: 9,
                height: { xs: "20px", xl: "30px" },
                padding: displayName.trim() ? "0.25rem 2rem 1rem" : "10px 15px",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 400,
                  opacity: displayName.trim() ? 1 : 0.6,
                  mr: 1,
                }}
              >
                {displayName}
              </Typography>

              <Box
                sx={{
                  padding: displayPrice ? "0" : "1px",
                  borderLeft: displayPrice
                    ? `1px solid ${theme?.palette?.companyColors?.primary}`
                    : "none",
                }}
              >
                {displayPrice !== null && displayPrice > 0 && (
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      fontSize: "1.25rem",
                      color: theme?.palette?.companyColors?.secondary,
                      opacity: 0.5,
                      ml: 1,
                      mt: "2px",
                    }}
                  >
                    {formatCurrency(displayPrice)}
                  </Typography>
                )}
              </Box>
            </Box>
          </LeftContent>
        </ContentLeft>

        <ContentRight>
          <SelectedSummary
            onHandleToggle={handleToggleInterface}
            showInterface={toggleInterface}
          />
        </ContentRight>
      </InteractiveButtonsRoot>
    )
  );
});

InteractiveButtons.defaultProps = { open: false };

export { InteractiveButtons };
export default InteractiveButtons;
